import { unseenReleaseNotes } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { markReleaseNotesSeen } from "../api.js";
import { locale } from "../locale.js";
import { useRefreshSession, useSignedInUser } from "../session.js";
import { actionButton } from "./controls.js";

/**
 * What has been released since the reader last said they had read — decided
 * 2026-10-17 in `project-scope.md`, asked for by the owner for the guests.
 *
 * **Shown only where the server sends the day**, which it does for an account
 * that owns no cellar and for nobody else; for everybody else this draws
 * nothing, and there is no rule to repeat here. **Dismissed by saying so**, not
 * by the page being shown once: a note seen on a phone in passing and not read
 * should still be there on the next visit. The day is kept on the account, so
 * it follows the person from one device to the next.
 *
 * A failed dismissal leaves the notes standing and says nothing more — the
 * button can be pressed again, and a note that stays is not a loss.
 */
export function WhatsNew() {
  const { t } = useTranslation();
  const user = useSignedInUser();
  const refresh = useRefreshSession();
  const [busy, setBusy] = useState(false);

  const seenAt = user?.releaseNotesSeenAt;
  if (seenAt === null || seenAt === undefined) return null;
  const notes = unseenReleaseNotes(seenAt);
  if (notes.length === 0) return null;

  const dismiss = () => {
    setBusy(true);
    void markReleaseNotesSeen()
      .then(() => refresh())
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <aside
      aria-label={t("releaseNotes.heading")}
      className="border-line bg-surface mb-6 max-w-prose border-y py-4"
    >
      <h2 className="font-display text-ink text-base">{t("releaseNotes.heading")}</h2>
      <ul className="mt-3 space-y-3">
        {notes.map((note) => (
          <li key={note.id}>
            <p className="text-sm">
              <span className="font-medium">{t(`releaseNotes.${note.id}.title`)}</span>
              <span className="text-muted">
                {" · "}
                {new Date(note.date).toLocaleDateString(locale(), {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </p>
            <p className="text-muted mt-0.5 text-sm">
              {t(`releaseNotes.${note.id}.body`)}
            </p>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={dismiss}
        disabled={busy}
        className={`${actionButton} mt-4`}
      >
        {t("releaseNotes.dismiss")}
      </button>
    </aside>
  );
}
